import React, { useMemo, useState } from 'react';
import { getCompanySlug } from '../../../client-experience/utils/companyAccess';
import {
  formatPortalSlugCooldownMessage,
  getActivePortalSlugCooldownStatus,
  getPortalSlugChangedAt,
  parsePortalSlugChangedAt
} from '../../../client-experience/utils/portalSlugCooldown';
import { resetPortalSlugCooldown } from '../api/companyProfileApi';
import './styles/PortalSlugCooldownAdminSection.css';

const formatDateTime = (value) => {
  const date = parsePortalSlugChangedAt(value);
  if (!date) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const PortalSlugCooldownAdminSection = ({ company, onCompanyUpdated }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const portalSlug = getCompanySlug(company);
  const cooldownStatus = useMemo(
    () => getActivePortalSlugCooldownStatus(company, portalSlug),
    [company, portalSlug]
  );
  const isLocked = !cooldownStatus.allowed;
  const lastChangedAt = getPortalSlugChangedAt(company);
  const lastAdminResetAt = parsePortalSlugChangedAt(company?.portalSettings?.cooldownResetAt);

  const handleResetCooldown = async () => {
    if (!company?.id || busy) {
      return;
    }

    const confirmed = window.confirm(
      `Reset the website name cooldown for "${company.name || company.id}"?\n\nThey will be able to change their website name (${portalSlug}) immediately.`
    );

    if (!confirmed) {
      return;
    }

    setBusy(true);
    setError('');
    setSuccess('');

    try {
      await resetPortalSlugCooldown(company.id);
      const resetChangedAt = new Date(Date.now() - (31 * 24 * 60 * 60 * 1000));

      onCompanyUpdated?.({
        ...company,
        portalSettings: {
          ...(company.portalSettings || {}),
          customUrlChangedAt: resetChangedAt,
          cooldownResetAt: new Date()
        }
      });

      setSuccess('Website name cooldown reset. The business can change their name now.');
    } catch (err) {
      console.error('Failed to reset portal slug cooldown:', err);
      setError(err?.message || 'Failed to reset website name cooldown.');
    } finally {
      setBusy(false);
    }
  };

  if (!company?.id) {
    return null;
  }

  return (
    <div className="portal-slug-admin">
      <div className="portal-slug-admin__header">
        <div>
          <h3 className="portal-slug-admin__title">Website name cooldown</h3>
          <p className="portal-slug-admin__subtitle">
            Businesses can only change their storefront website name once every 30 days.
          </p>
        </div>
        {portalSlug ? (
          <button
            type="button"
            className="at-btn portal-slug-admin__reset-btn"
            onClick={handleResetCooldown}
            disabled={busy || !isLocked}
            title={isLocked ? 'Allow an immediate website name change' : 'No active cooldown to reset'}
          >
            {busy ? 'Resetting…' : 'Reset cooldown'}
          </button>
        ) : null}
      </div>

      <div className="portal-slug-admin__grid">
        <div className="portal-slug-admin__item">
          <span className="portal-slug-admin__label">Website name</span>
          <span className="portal-slug-admin__value">{portalSlug || 'Not set yet'}</span>
        </div>
        <div className="portal-slug-admin__item">
          <span className="portal-slug-admin__label">Cooldown status</span>
          <span className={`portal-slug-admin__value portal-slug-admin__value--${isLocked ? 'locked' : 'open'}`}>
            {portalSlug
              ? (isLocked ? 'Locked' : 'Can change now')
              : '—'}
          </span>
        </div>
        <div className="portal-slug-admin__item">
          <span className="portal-slug-admin__label">Last name change</span>
          <span className="portal-slug-admin__value">{formatDateTime(lastChangedAt)}</span>
        </div>
        <div className="portal-slug-admin__item">
          <span className="portal-slug-admin__label">Last admin reset</span>
          <span className="portal-slug-admin__value">{formatDateTime(lastAdminResetAt)}</span>
        </div>
      </div>

      {portalSlug && isLocked ? (
        <div className="portal-slug-admin__notice portal-slug-admin__notice--locked">
          {formatPortalSlugCooldownMessage(cooldownStatus)}
        </div>
      ) : null}

      {portalSlug && !isLocked ? (
        <div className="portal-slug-admin__notice">
          No active cooldown. Reset is only needed if they are currently locked out from changing their website name.
        </div>
      ) : null}

      {!portalSlug ? (
        <div className="portal-slug-admin__notice">
          This company has not claimed a website name yet, so there is no cooldown to reset.
        </div>
      ) : null}

      {error ? (
        <div className="portal-slug-admin__alert portal-slug-admin__alert--error" role="alert">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="portal-slug-admin__alert portal-slug-admin__alert--success" role="status">
          {success}
        </div>
      ) : null}
    </div>
  );
};

export default PortalSlugCooldownAdminSection;
